import { TeamInLeague, Result, Player, Tactics, MatchEvent, GameState, TacticalPosition } from '../domain/entities';
import { FlavorTextService } from './FlavorTextService';
import { FORMATIONS, POSITION_MAP } from '../domain/constants';
import { calculateMatchPerformance, calculatePositionalPenalty } from '../utils/utilities';

interface TeamComposition {
    attack: number;
    mid: number;
    def: number;
    startingXI: Player[];
    gk: Player | undefined;
    avgSkill: number;
    stamina: number;
}

interface MatchSimulationResult {
    result: Result;
    homeGkId: number | null;
    awayGkId: number | null;
}

const getTeamComposition = (team: TeamInLeague): TeamComposition => {
    // Players with severe injuries or suspensions are unavailable
    const availablePlayers = [...team.squad.filter(p => p.injury?.type !== 'Severe' && !p.suspension)];
    const formationPositions = FORMATIONS[team.tactics.formation] || [];
    const finalLineup: Record<TacticalPosition, Player | null> = {};
    const assignedPlayerIds = new Set<number>();

    // Step 1: Honor existing lineup as much as possible, if players are available
    formationPositions.forEach(pos => {
        const playerId = team.tactics.lineup[pos as TacticalPosition];
        const player = playerId !== null ? availablePlayers.find(p => p.id === playerId && !assignedPlayerIds.has(p.id)) : undefined;
        if (player) {
            finalLineup[pos] = player;
            assignedPlayerIds.add(player.id);
        } else {
            finalLineup[pos] = null;
        }
    });

    const availableForFilling = availablePlayers.filter(p => !assignedPlayerIds.has(p.id));

    // Step 2: Fill empty slots with best available players (only matching position)
    formationPositions.forEach(pos => {
        if (finalLineup[pos] === null) {
            const requiredGeneralPos = POSITION_MAP[pos as TacticalPosition];

            let bestPlayer: Player | undefined;
            let bestPlayerIndex = -1;
            let bestScore = -Infinity;

            // Only consider players who play the required position
            availableForFilling.forEach((p, index) => {
                if (p.position === requiredGeneralPos) {
                    const score = p.skill + p.form;

                    if (score > bestScore) {
                        bestScore = score;
                        bestPlayer = p;
                        bestPlayerIndex = index;
                    }
                }
            });

            if (bestPlayer) {
                finalLineup[pos] = bestPlayer;
                availableForFilling.splice(bestPlayerIndex, 1);
            }
        }
    });

    // Step 3: Fill any remaining slots with emergency youth players
    let emergencyPlayerId = -1;
    formationPositions.forEach(pos => {
        if (finalLineup[pos] === null) {
            const emergencyPlayer: Player = {
                id: emergencyPlayerId--, name: "Youth Player", position: POSITION_MAP[pos as TacticalPosition] || 'MID', skill: 25, potential: 40,
                age: 16,
                form: 0,
                fitness: 100,
                fitnessMax: 80,
                fitnessRecovery: 10,
                wage: 0,
                expectedWage: 0,
                hue: Math.floor(Math.random() * 360),
                faceEmoji: '🧑',
                attributes: {}, injury: null, suspension: null, gamesPlayed: 0, goalsConceded: 0, goalsScored: 0, history: [], lastSeasonChange: 0,
                morale: 'Content',
                personality: 'Professional',
                gender: team.squad[0]?.gender || 'male',
                recentRatings: [],
                assists: 0,
                yellowCards: 0,
                redCards: 0,
                yellowCardAccumulation: 0,
                contract: { yearsRemaining: 1, expirySeason: 9999 },
                potmAwards: 0,
                marketValue: 0,
                marketValueHistory: [],
                favoredFoot: 'Right',
                favoredPositionCategory: 'Any',
                developmentHistory: [],
                injuryHistory: [],
                flexibility: {
                    positionFlexibility: 50,
                    categoryFlexibility: 50,
                    footFlexibility: 50,
                },
            };
            finalLineup[pos] = emergencyPlayer;
        }
    });

    const startingXI = Object.values(finalLineup).filter((p): p is Player => p !== null);

    const strengths = { attack: 0, mid: 0, def: 0 };
    const playerCount = { attack: 0, mid: 0, def: 0 };
    let gk: Player | undefined;
    let totalSkill = 0;
    let totalStamina = 0;

    Object.entries(finalLineup).forEach(([tacticalPos, player]) => {
        if (!player) return;

        const basePerf = calculateMatchPerformance(player);
        const positionalPenalty = calculatePositionalPenalty(player, tacticalPos as TacticalPosition);
        const finalScore = basePerf * (1 - positionalPenalty);
        
        const generalPos = POSITION_MAP[tacticalPos as TacticalPosition] || player.position;

        if (generalPos === 'GK') { 
            strengths.def += finalScore; 
            playerCount.def++; 
            if (!gk || player.id >= 0) gk = player; // Prioritize real GKs
        }
        else if (generalPos === 'DEF') { strengths.def += finalScore; playerCount.def++; }
        else if (generalPos === 'MID') { strengths.mid += finalScore; playerCount.mid++; }
        else if (generalPos === 'FWD') { strengths.attack += finalScore; playerCount.attack++; }

        totalSkill += finalScore;
        totalStamina += player.fitnessMax;
    });

    if (!gk) {
        strengths.def /= 2; // Heavily penalize not having a goalkeeper
    }

    const avgStamina = totalStamina / (startingXI.length || 1);

    return {
        attack: playerCount.attack > 0 ? strengths.attack / playerCount.attack : 40,
        mid: playerCount.mid > 0 ? strengths.mid / playerCount.mid : 40,
        def: playerCount.def > 0 ? strengths.def / playerCount.def : 40,
        startingXI,
        gk,
        avgSkill: totalSkill / (startingXI.length || 1),
        stamina: avgStamina,
    };
};

const applyTactics = (composition: Omit<TeamComposition, 'avgSkill' | 'stamina'>, tactics: Tactics): Omit<TeamComposition, 'avgSkill'| 'stamina'> => {
    const newComposition = { ...composition };
    // Mentality
    switch (tactics.mentality) {
        case 'Attacking':
            newComposition.attack *= 1.1;
            newComposition.mid *= 1.05;
            newComposition.def *= 0.9;
            break;
        case 'Defensive':
            newComposition.attack *= 0.9;
            newComposition.mid *= 0.95;
            newComposition.def *= 1.1;
            break;
    }

    // Defensive Style
    switch (tactics.defensiveStyle) {
        case 'High Press':
            newComposition.def *= 1.05; // Better at winning ball back
            newComposition.mid *= 1.05;
            break;
        case 'Deep Block':
            newComposition.def *= 1.1; // Harder to score against
            newComposition.attack *= 0.95; // Less attacking threat
            break;
    }

    return newComposition;
}

export const simulateMatch = (homeTeam: TeamInLeague, awayTeam: TeamInLeague, gameState?: GameState): MatchSimulationResult => {
    let homeCompData = getTeamComposition(homeTeam);
    let awayCompData = getTeamComposition(awayTeam);
    
    let homeComposition = applyTactics(homeCompData, homeTeam.tactics);
    let awayComposition = applyTactics(awayCompData, awayTeam.tactics);

    // Home advantage
    const homeStrength = {
        attack: homeComposition.attack + 2,
        mid: homeComposition.mid + 2,
        def: homeComposition.def + 2,
    };
    const awayStrength = {
        attack: awayComposition.attack,
        mid: awayComposition.mid,
        def: awayComposition.def,
    };

    // Tactical counter-play for High Press vs Pace
    const homeFwdPace = homeCompData.startingXI.filter(p => p.position === 'FWD').reduce((sum, p) => sum + (p.attributes.pace || 0), 0) / (homeCompData.startingXI.filter(p => p.position === 'FWD').length || 1);
    if (awayTeam.tactics.defensiveStyle === 'High Press' && homeFwdPace > 80) {
        homeStrength.attack *= 1.05; // 5% bonus against high line with pace
    }

    const awayFwdPace = awayCompData.startingXI.filter(p => p.position === 'FWD').reduce((sum, p) => sum + (p.attributes.pace || 0), 0) / (awayCompData.startingXI.filter(p => p.position === 'FWD').length || 1);
    if (homeTeam.tactics.defensiveStyle === 'High Press' && awayFwdPace > 80) {
        awayStrength.attack *= 1.05;
    }

    const homeAttackVsAwayDef = homeStrength.attack - awayStrength.def;
    const awayAttackVsHomeDef = awayStrength.attack - homeStrength.def;
    const homeMidAdvantage = (homeStrength.mid - awayStrength.mid);
    const staminaAdvantage = homeCompData.stamina - awayCompData.stamina;

    let baseChances = 14;
    // Passing style
    if (homeTeam.tactics.passingStyle === 'Direct') baseChances += 2;
    if (awayTeam.tactics.passingStyle === 'Direct') baseChances += 2;
    if (homeTeam.tactics.passingStyle === 'Short') baseChances -= 2;
    if (awayTeam.tactics.passingStyle === 'Short') baseChances -= 2;
    
    if (homeTeam.tactics.mentality === 'Attacking') baseChances += 2;
    if (awayTeam.tactics.mentality === 'Attacking') baseChances += 2;
    if (homeTeam.tactics.mentality === 'Defensive') baseChances -= 2;
    if (awayTeam.tactics.mentality === 'Defensive') baseChances -= 2;
    const totalChances = Math.max(8, baseChances + Math.floor(Math.random() * 8));

    const midControlFactor = Math.max(0.2, Math.min(0.8, 0.5 + (homeMidAdvantage / 20) + (staminaAdvantage / 100)));
    const homeChances = Math.round(totalChances * midControlFactor);
    const awayChances = totalChances - homeChances;

    let homeConversionRate = Math.max(0.02, Math.min(0.3, 0.08 + (homeAttackVsAwayDef / 250) + (staminaAdvantage / 500)));
    let awayConversionRate = Math.max(0.02, Math.min(0.3, 0.07 + (awayAttackVsHomeDef / 250) - (staminaAdvantage / 500)));
    
    // Passing style effect on conversion
    if (homeTeam.tactics.passingStyle === 'Direct') homeConversionRate *= 0.95; // More chances, but lower quality
    if (homeTeam.tactics.passingStyle === 'Short') homeConversionRate *= 1.05; // Fewer chances, higher quality
    if (awayTeam.tactics.passingStyle === 'Direct') awayConversionRate *= 0.95;
    if (awayTeam.tactics.passingStyle === 'Short') awayConversionRate *= 1.05;

    let homeScore = 0;
    let awayScore = 0;
    const events: MatchEvent[] = [{ minute: 0, message: "Kick off!", type: 'info', team: 'ref', soundEffect: 'whistle' }];
    
    for (let i = 0; i < homeChances; i++) {
        if (Math.random() < homeConversionRate) homeScore++;
    }
    
    for (let i = 0; i < awayChances; i++) {
        if (Math.random() < awayConversionRate) awayScore++;
    }

    // "Always Win" Debug Mode
    if (gameState?.debugFlags?.alwaysWin && gameState.gameStarted) {
        if (homeTeam.name === gameState.playerTeamName) {
            if (homeScore <= awayScore) {
                homeScore = awayScore + 1 + Math.floor(Math.random() * 2);
            }
        } else if (awayTeam.name === gameState.playerTeamName) {
            if (awayScore <= homeScore) {
                awayScore = homeScore + 1 + Math.floor(Math.random() * 2);
            }
        }
    }

    // Generate cards first so we can exclude dismissed players from scorers
    const yellowCards: Result['yellowCards'] = [];
    const redCards: Result['redCards'] = [];
    const allPlayersInMatch = [...homeCompData.startingXI, ...awayCompData.startingXI];
    const bookedPlayerIds = new Set<number>();
    const sentOffPlayerIds = new Set<number>(); // Track sent off players

    for (let i = 0; i < 5; i++) { // 5 potential card incidents in a match
        const eligiblePlayers = allPlayersInMatch.filter(p => !sentOffPlayerIds.has(p.id));
        if (eligiblePlayers.length === 0) break;

        const player = eligiblePlayers[Math.floor(Math.random() * eligiblePlayers.length)];
        if (player.id < 0) continue;

        let cardChance = 0.05;
        if (player.personality === 'Hot-headed') cardChance += 0.1;
        if (player.position === 'DEF') cardChance += 0.05;

        const team = homeCompData.startingXI.some(p => p.id === player.id) ? homeTeam : awayTeam;

        if (team.tactics.tacklingStyle === 'Aggressive') cardChance += 0.08;
        if (team.tactics.tacklingStyle === 'Cautious') cardChance -= 0.04;

        if (team.tactics.mentality === 'Defensive') cardChance += 0.03;
        if (team.tactics.mentality === 'Attacking') cardChance -= 0.02;

        if (Math.random() < cardChance) {
            const minute = Math.floor(Math.random() * 90) + 1;
            const teamName = team.name;

            // Straight red card possibility (5% of card incidents)
            const isStraightRed = Math.random() < 0.05;

            if (isStraightRed) {
                redCards.push({ minute, playerId: player.id });
                sentOffPlayerIds.add(player.id);
                events.push({ minute, message: `Red Card for ${player.name} (${teamName})! Serious foul play.`, type: 'red_card', team: teamName, soundEffect: 'card' });
            } else if (bookedPlayerIds.has(player.id)) { // Second yellow -> red
                if (!redCards.some(c => c.playerId === player.id)) {
                    redCards.push({ minute, playerId: player.id });
                    sentOffPlayerIds.add(player.id);
                    events.push({ minute, message: `Red Card for ${player.name} (${teamName})! Second yellow.`, type: 'red_card', team: teamName, soundEffect: 'card' });
                }
            } else {
                yellowCards.push({ minute, playerId: player.id });
                bookedPlayerIds.add(player.id);
                events.push({ minute, message: `Yellow Card for ${player.name} (${teamName}).`, type: 'yellow_card', team: teamName, soundEffect: 'card' });
            }
        }
    }

    // Now select scorers, excluding dismissed players
    const homeScorers: number[] = [];
    const awayScorers: number[] = [];

    const homePotentialScorers = homeCompData.startingXI.filter(p => (p.position === 'FWD' || p.position === 'MID') && p.id >= 0 && !sentOffPlayerIds.has(p.id));
    if (homePotentialScorers.length > 0) {
        let weightedScorers = homePotentialScorers.flatMap(p => {
            const lineupPos = Object.keys(homeTeam.tactics.lineup).find(pos => homeTeam.tactics.lineup[pos as any] === p.id);
            let weight = 1;
            if (p.position === 'FWD') weight = 2;

            if (homeTeam.tactics.attackingFocus === 'Through Middle' && lineupPos && (lineupPos.includes('ST') || lineupPos.includes('CM'))) weight *= 1.5;
            if ((homeTeam.tactics.attackingFocus === 'Down Left Flank' && lineupPos && (lineupPos.includes('L') || lineupPos.includes('W')))) weight *= 1.5;
            if ((homeTeam.tactics.attackingFocus === 'Down Right Flank' && lineupPos && (lineupPos.includes('R') || lineupPos.includes('W')))) weight *= 1.5;
            return Array(Math.round(weight)).fill(p);
        });

        if (weightedScorers.length === 0) weightedScorers = homePotentialScorers;

        for (let i = 0; i < homeScore; i++) {
            const scorer = weightedScorers[Math.floor(Math.random() * weightedScorers.length)];
            homeScorers.push(scorer.id);
            events.push({ minute: -1, message: `GOAL! ${scorer.name} scores (${homeTeam.name})!`, type: 'goal', team: homeTeam.name, isGoal: true, soundEffect: 'goal' });
        }
    }

    const awayPotentialScorers = awayCompData.startingXI.filter(p => (p.position === 'FWD' || p.position === 'MID') && p.id >= 0 && !sentOffPlayerIds.has(p.id));
    if (awayPotentialScorers.length > 0) {
        let weightedScorers = awayPotentialScorers.flatMap(p => {
            const lineupPos = Object.keys(awayTeam.tactics.lineup).find(pos => awayTeam.tactics.lineup[pos as any] === p.id);
            let weight = 1;
            if (p.position === 'FWD') weight = 2;

            if (awayTeam.tactics.attackingFocus === 'Through Middle' && lineupPos && (lineupPos.includes('ST') || lineupPos.includes('CM'))) weight *= 1.5;
            if ((awayTeam.tactics.attackingFocus === 'Down Left Flank' && lineupPos && (lineupPos.includes('L') || lineupPos.includes('W')))) weight *= 1.5;
            if ((awayTeam.tactics.attackingFocus === 'Down Right Flank' && lineupPos && (lineupPos.includes('R') || lineupPos.includes('W')))) weight *= 1.5;
            return Array(Math.round(weight)).fill(p);
        });

        if (weightedScorers.length === 0) weightedScorers = awayPotentialScorers;

        for (let i = 0; i < awayScore; i++) {
            const scorer = weightedScorers[Math.floor(Math.random() * weightedScorers.length)];
            awayScorers.push(scorer.id);
            events.push({ minute: -1, message: `GOAL! ${scorer.name} scores (${awayTeam.name})!`, type: 'goal', team: awayTeam.name, isGoal: true, soundEffect: 'goal' });
        }
    }

    const homeAssists: Result['homeAssists'] = [];
    homeScorers.forEach(scorerId => {
        const potentialAssisters = homeCompData.startingXI.filter(p => p.id !== scorerId && p.position !== 'GK' && p.id >= 0 && !sentOffPlayerIds.has(p.id));
        if (potentialAssisters.length > 0) {
            const assister = potentialAssisters[Math.floor(Math.random() * potentialAssisters.length)];
            homeAssists.push({ assisterId: assister.id, goalScorerId: scorerId });
        }
    });

    const awayAssists: Result['awayAssists'] = [];
    awayScorers.forEach(scorerId => {
        const potentialAssisters = awayCompData.startingXI.filter(p => p.id !== scorerId && p.position !== 'GK' && p.id >= 0 && !sentOffPlayerIds.has(p.id));
        if (potentialAssisters.length > 0) {
            const assister = potentialAssisters[Math.floor(Math.random() * potentialAssisters.length)];
            awayAssists.push({ assisterId: assister.id, goalScorerId: scorerId });
        }
    });


    // Manager Influence Events
    const managerEventChance = 0.2; // 20% chance of a manager event happening
    if (Math.random() < managerEventChance) {
        const isHomeEvent = Math.random() < 0.5;
        const team = isHomeEvent ? homeTeam : awayTeam;
        const isPositive = (Math.random() * 100) < team.boardConfidence;
        const message = FlavorTextService.getManagerialActionText(isPositive);
        events.push({
            minute: 10 + Math.floor(Math.random() * 70),
            message: message,
            type: isPositive ? 'positive' : 'negative',
            team: team.name
        });
    }

    events.push({ minute: 45, message: "Half Time", type: 'info', team: 'ref', soundEffect: 'whistle' });
    events.push({ minute: 90, message: "Full Time", type: 'info', team: 'ref', soundEffect: 'whistle' });

    // Assign random minutes and sort all events
    events.forEach(event => {
        if (event.minute === -1) {
            event.minute = Math.floor(Math.random() * 90) + 1;
        }
    });
    events.sort((a, b) => a.minute - b.minute);

    // Iterate through sorted events to add running score to goals
    let runningHomeScore = 0;
    let runningAwayScore = 0;
    events.forEach(event => {
        if (event.isGoal) {
            if (event.team === homeTeam.name) {
                runningHomeScore++;
            } else {
                runningAwayScore++;
            }
            event.homeScore = runningHomeScore;
            event.awayScore = runningAwayScore;
        }
    });

    const homeStartingXI_Ids = homeCompData.startingXI.map(p => p.id);
    const awayStartingXI_Ids = awayCompData.startingXI.map(p => p.id);
    
    const homeShots = homeChances;
    const awayShots = awayChances;

    const homeShotsOnTarget = Math.max(homeScore, Math.floor(homeShots * (0.2 + Math.random() * 0.4)));
    const awayShotsOnTarget = Math.max(awayScore, Math.floor(awayShots * (0.2 + Math.random() * 0.4)));

    const homePossession = Math.round(midControlFactor * 100);
    const awayPossession = 100 - homePossession;

    const calculateRatings = (
        startingXI: Player[], isWin: boolean, isDraw: boolean, score: number, conceded: number, scorers: number[],
    ): { [playerId: number]: number } => {
        const ratings: { [playerId: number]: number } = {};
        const resultModifier = isWin ? 1 : isDraw ? 0 : -0.5;

        startingXI.forEach(player => {
            if (player.id < 0) return; // Don't rate emergency youth players

            let rating = 6.0;
            rating += resultModifier;

            if (player.position === 'FWD' || player.position === 'MID') {
                const goals = scorers.filter(id => id === player.id).length;
                rating += goals * 1.5;
            }
            if (player.position === 'DEF' || player.position === 'GK') {
                if (conceded === 0) rating += 1.0;
                rating -= conceded * 0.4;
            }

            rating += (Math.random() - 0.5); // +- 0.5 random noise
            ratings[player.id] = Math.max(3.0, Math.min(10.0, parseFloat(rating.toFixed(1))));
        });
        return ratings;
    };

    const homePlayerRatings = calculateRatings(homeCompData.startingXI, homeScore > awayScore, homeScore === awayScore, homeScore, awayScore, homeScorers);
    const awayPlayerRatings = calculateRatings(awayCompData.startingXI, awayScore > homeScore, homeScore === awayScore, awayScore, homeScore, awayScorers);

    const allRatings = [
        ...Object.entries(homePlayerRatings).map(([id, rating]) => ({ id: Number(id), rating, teamName: homeTeam.name })),
        ...Object.entries(awayPlayerRatings).map(([id, rating]) => ({ id: Number(id), rating, teamName: awayTeam.name }))
    ];
    
    let potm: Result['potm'] | undefined;
    if (allRatings.length > 0) {
        // Filter out dismissed players from POTM selection
        const potmEligible = allRatings.filter(r => !sentOffPlayerIds.has(r.id));
        if (potmEligible.length > 0) {
            const sortedRatings = potmEligible.sort((a, b) => b.rating - a.rating);
            const topPlayer = sortedRatings[0];
            potm = {
                playerId: topPlayer.id,
                teamName: topPlayer.teamName,
                rating: topPlayer.rating,
            };
        }
    }
    
    const skillDiff = homeCompData.avgSkill - awayCompData.avgSkill;
    const isUpset = (homeScore > awayScore && skillDiff < -8) || (awayScore > homeScore && skillDiff > 8);
    const isThriller = homeScore + awayScore >= 6;
    const isSignificant = isUpset || isThriller;

    return {
        result: { 
            homeScore, awayScore, homeScorers, awayScorers,
            homeStartingXI: homeStartingXI_Ids,
            awayStartingXI: awayStartingXI_Ids,
            homePossession,
            awayPossession,
            homeShots,
            awayShots,
            homeShotsOnTarget,
            awayShotsOnTarget,
            homePlayerRatings,
            awayPlayerRatings,
            homeAssists,
            awayAssists,
            yellowCards,
            redCards,
            events,
            potm,
            isSignificant,
        },
        homeGkId: homeCompData.gk?.id || null,
        awayGkId: awayCompData.gk?.id || null,
    };
};

const pickRandom = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];

const cupFinalFlavorText: Record<string, string[]> = {
    '0-15': ["A cagey opening, both teams are just feeling each other out.", "Nervy start from both sides, no one wants to make the first mistake in the final.", "An early corner comes to nothing. The atmosphere is electric!", "The favorite is trying to assert dominance early on.", "An early speculative shot goes well wide."],
    '16-30': ["The game is starting to open up now.", "A crunching tackle in midfield fires up the crowd.", "First real chance! The keeper makes a comfortable save.", "A promising attack breaks down with a misplaced pass.", "The underdog is growing into this game.", "A moment of trickery from the winger, but the cross is cleared."],
    '31-45': ["The tempo has really picked up as we approach half time.", "A shot from distance fizzes just wide of the post!", "The referee is letting the game flow, much to the fans' delight.", "One team is starting to dominate possession now.", "The pressure is mounting... can they hold on until the break?", "A free-kick in a dangerous area... but it's straight into the wall."],
    '46-60': ["The second half is underway, let's see if the managers have changed things up.", "A tactical foul stops a dangerous counter-attack.", "The crowd roars for a penalty, but the referee waves play on!", "A moment of real quality in midfield unlocks the defence, but the final ball is lacking.", "Both sets of fans are in full voice, it's a tremendous atmosphere.", "An audacious long-range effort sails over the bar."],
    '61-75': ["Legs are starting to tire now, we could see some substitutions soon.", "It's getting tense, every pass is crucial.", "A glorious chance goes begging! How did he miss that?", "The goalkeeper pulls off a world-class save to keep the scores level!", "The game is on a knife edge. One mistake could decide it all.", "A brilliant defensive block denies a certain goal!"],
    '76-90': ["Into the final minutes, who will be the hero?", "The fourth official indicates added time. Is there a late twist in this tale?", "It's backs against the wall for one team now!", "The tension is unbearable! Every fan is on the edge of their seat.", "One last chance to be a hero...", "Cries for a handball in the box, but nothing is given!"]
};

export const generateDetailedEvents = (result: Result | undefined, homeTeamName: string, awayTeamName: string): MatchEvent[] => {
    if (!result || !result.events) {
        return [];
    }

    const detailedEvents: MatchEvent[] = [];
    const keyEventMinutes = new Set(result.events.map(e => e.minute));

    const flavorIntervals = [8, 15, 23, 31, 39, 44, 52, 58, 67, 74, 81, 88]; // More intervals for more detail

    flavorIntervals.forEach(minute => {
        if (!keyEventMinutes.has(minute)) {
            let period: string;
            if (minute <= 15) period = '0-15';
            else if (minute <= 30) period = '16-30';
            else if (minute <= 45) period = '31-45';
            else if (minute <= 60) period = '46-60';
            else if (minute <= 75) period = '61-75';
            else period = '76-90';

            detailedEvents.push({
                minute,
                message: pickRandom(cupFinalFlavorText[period]),
                type: 'info',
                team: 'commentator'
            });
        }
    });

    const allEvents = [...detailedEvents, ...result.events];
    allEvents.sort((a, b) => a.minute - b.minute);

    // Ensure no two events have the same minute to prevent visual overlap issues
    for (let i = 1; i < allEvents.length; i++) {
        if (allEvents[i].minute <= allEvents[i - 1].minute) {
            allEvents[i].minute = allEvents[i - 1].minute + 1;
        }
    }
    
    // Ensure final whistle is the very last event
    const fullTimeEvent = allEvents.find(e => e.message === "Full Time");
    if (fullTimeEvent) {
        fullTimeEvent.minute = Math.max(90, ...allEvents.map(e => e.minute)) + 1;
    }

    return allEvents.sort((a, b) => a.minute - b.minute);
};

export const simulatePenaltyShootout = (
    homeTeam: TeamInLeague,
    awayTeam: TeamInLeague,
    homeStartingXI: Player[],
    awayStartingXI: Player[],
    sentOffPlayerIds: Set<number> = new Set()
): { homeScore: number; awayScore: number; events: MatchEvent[] } => {
    const events: MatchEvent[] = [];
    let homeScore = 0;
    let awayScore = 0;

    // Take the top 5 outfield players from each starting XI based on skill (excluding sent-off players)
    const homePenaltyTakers = homeStartingXI
        .filter(p => p.position !== 'GK' && !sentOffPlayerIds.has(p.id))
        .sort((a, b) => b.skill - a.skill)
        .slice(0, 5);
    const awayPenaltyTakers = awayStartingXI
        .filter(p => p.position !== 'GK' && !sentOffPlayerIds.has(p.id))
        .sort((a, b) => b.skill - a.skill)
        .slice(0, 5);

    const homeGK = homeStartingXI.find(p => p.position === 'GK');
    const awayGK = awayStartingXI.find(p => p.position === 'GK');

    // Initial 5 penalties each
    for (let round = 0; round < 5; round++) {
        const homeTaker = homePenaltyTakers[round];
        const awayTaker = awayPenaltyTakers[round];

        // Home penalty
        if (homeTaker) {
            const homeChance = Math.min(0.85, 0.6 + (homeTaker.skill / 200));
            const homeScored = Math.random() < homeChance;

            if (homeScored) {
                homeScore++;
                events.push({
                    minute: 91 + (round * 2),
                    message: `[${homeTeam.name}] ${homeTaker.name} scores!`,
                    type: 'penalty_scored',
                    team: homeTeam.name,
                    homeScore,
                    awayScore,
                });
            } else {
                events.push({
                    minute: 91 + (round * 2),
                    message: `[${homeTeam.name}] ${homeTaker.name} misses!`,
                    type: 'penalty_missed',
                    team: homeTeam.name,
                    homeScore,
                    awayScore,
                });
            }
        }

        // Away penalty
        if (awayTaker) {
            const awayChance = Math.min(0.85, 0.6 + (awayTaker.skill / 200));
            const awayScored = Math.random() < awayChance;

            if (awayScored) {
                awayScore++;
                events.push({
                    minute: 91 + (round * 2) + 1,
                    message: `[${awayTeam.name}] ${awayTaker.name} scores!`,
                    type: 'penalty_scored',
                    team: awayTeam.name,
                    homeScore,
                    awayScore,
                });
            } else {
                events.push({
                    minute: 91 + (round * 2) + 1,
                    message: `[${awayTeam.name}] ${awayTaker.name} misses!`,
                    type: 'penalty_missed',
                    team: awayTeam.name,
                    homeScore,
                    awayScore,
                });
            }
        }

        // Check if one team has already won
        const remainingPenalties = 5 - (round + 1);
        if (homeScore > awayScore + remainingPenalties || awayScore > homeScore + remainingPenalties) {
            break;
        }
    }

    // Sudden death if still tied (only if both teams have penalty takers available)
    if (homePenaltyTakers.length > 0 && awayPenaltyTakers.length > 0) {
        let suddenDeathRound = 0;
        while (homeScore === awayScore && suddenDeathRound < 5) {
            const homeTaker = homePenaltyTakers[suddenDeathRound % homePenaltyTakers.length];
            const awayTaker = awayPenaltyTakers[suddenDeathRound % awayPenaltyTakers.length];

            const homeChance = Math.min(0.85, 0.6 + (homeTaker.skill / 200));
            const homeScored = Math.random() < homeChance;

            if (homeScored) {
                homeScore++;
                events.push({
                    minute: 101 + suddenDeathRound * 2,
                    message: `[${homeTeam.name}] ${homeTaker.name} scores!`,
                    type: 'penalty_scored',
                    team: homeTeam.name,
                    homeScore,
                    awayScore,
                });
            } else {
                events.push({
                    minute: 101 + suddenDeathRound * 2,
                    message: `[${homeTeam.name}] ${homeTaker.name} misses!`,
                    type: 'penalty_missed',
                    team: homeTeam.name,
                    homeScore,
                    awayScore,
                });
            }

            const awayChance = Math.min(0.85, 0.6 + (awayTaker.skill / 200));
            const awayScored = Math.random() < awayChance;

            if (awayScored) {
                awayScore++;
                events.push({
                    minute: 101 + suddenDeathRound * 2 + 1,
                    message: `[${awayTeam.name}] ${awayTaker.name} scores!`,
                    type: 'penalty_scored',
                    team: awayTeam.name,
                    homeScore,
                    awayScore,
                });
            } else {
                events.push({
                    minute: 101 + suddenDeathRound * 2 + 1,
                    message: `[${awayTeam.name}] ${awayTaker.name} misses!`,
                    type: 'penalty_missed',
                    team: awayTeam.name,
                    homeScore,
                    awayScore,
                });
            }

            suddenDeathRound++;
        }
    }

    // If still tied after sudden death, just pick a winner randomly
    if (homeScore === awayScore) {
        if (Math.random() < 0.5) {
            homeScore++;
        } else {
            awayScore++;
        }
    }

    return { homeScore, awayScore, events };
};

/**
 * Calculate team strengths by position - uses same algorithm as match engine
 * Returns average effective performance for GK, DEF, MID, FWD positions
 */
export const calculateTeamStrengths = (team: TeamInLeague): {
    gk: number;
    def: number;
    mid: number;
    fwd: number;
} => {
    const composition = getTeamComposition(team);
    const tacticsApplied = applyTactics(composition, team.tactics);

    // Extract position-specific strengths
    const strengths = { gk: 0, def: 0, mid: 0, fwd: 0 };
    const playerCount = { gk: 0, def: 0, mid: 0, fwd: 0 };

    Object.entries(team.tactics.lineup).forEach(([tacticalPos, playerId]) => {
        if (playerId === null) return;

        const player = composition.startingXI.find(p => p.id === playerId);
        if (!player) return;

        const basePerf = calculateMatchPerformance(player);
        const positionalPenalty = calculatePositionalPenalty(player, tacticalPos as TacticalPosition);
        const finalScore = basePerf * (1 - positionalPenalty);

        const generalPos = POSITION_MAP[tacticalPos as TacticalPosition] || player.position;

        if (generalPos === 'GK') {
            strengths.gk += finalScore;
            playerCount.gk++;
        } else if (generalPos === 'DEF') {
            strengths.def += finalScore;
            playerCount.def++;
        } else if (generalPos === 'MID') {
            strengths.mid += finalScore;
            playerCount.mid++;
        } else if (generalPos === 'FWD') {
            strengths.fwd += finalScore;
            playerCount.fwd++;
        }
    });

    // Apply tactical modifiers (same as match engine)
    let gkStrength = playerCount.gk > 0 ? strengths.gk / playerCount.gk : 40;
    let defStrength = playerCount.def > 0 ? strengths.def / playerCount.def : 40;
    let midStrength = playerCount.mid > 0 ? strengths.mid / playerCount.mid : 40;
    let fwdStrength = playerCount.fwd > 0 ? strengths.fwd / playerCount.fwd : 40;

    // GK contributes to defense in match engine
    defStrength = (defStrength * playerCount.def + gkStrength) / (playerCount.def + 1);

    // Apply mentality modifiers
    switch (team.tactics.mentality) {
        case 'Attacking':
            fwdStrength *= 1.1;
            midStrength *= 1.05;
            defStrength *= 0.9;
            gkStrength *= 0.9;
            break;
        case 'Defensive':
            fwdStrength *= 0.9;
            midStrength *= 0.95;
            defStrength *= 1.1;
            gkStrength *= 1.1;
            break;
    }

    // Apply defensive style modifiers
    switch (team.tactics.defensiveStyle) {
        case 'High Press':
            defStrength *= 1.05;
            midStrength *= 1.05;
            break;
        case 'Deep Block':
            defStrength *= 1.1;
            gkStrength *= 1.1;
            fwdStrength *= 0.95;
            break;
    }

    return {
        gk: Math.round(gkStrength * 10) / 10,
        def: Math.round(defStrength * 10) / 10,
        mid: Math.round(midStrength * 10) / 10,
        fwd: Math.round(fwdStrength * 10) / 10,
    };
};